/**
 * Learn Mode — tour state.
 *
 * The tour runs ON the real pages. It owns three things and nothing else:
 * which step we are on, whether demo mode is serving fixtures, and the plumbing
 * to move between steps. The pages themselves are untouched apart from a
 * `data-tour` attribute on the controls a step points at.
 *
 * Demo mode is flipped BEFORE the first navigation and cleared after the last,
 * so no real record is ever read or written while a lesson is running.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { setDemoMode } from './demoAdapter';
import { applyBeat, resetBlock, resetFixtures, setDemoPath } from './fixtures';
import { resetPerformanceScopes } from '../hooks/usePerformanceScopes';
import useIsDesktop from '../hooks/useIsDesktop';
import { lessonById, stepsFor } from './tour';

const Ctx = createContext(null);
export const useTour = () => useContext(Ctx);

const KEY = 'cid.learn.progress';
export const readProgress = () => {
  try { return JSON.parse(localStorage.getItem(KEY) || '{}'); } catch { return {}; }
};
const writeProgress = (p) => {
  try { localStorage.setItem(KEY, JSON.stringify(p)); } catch { /* private mode: the lesson runs, it just won't resume */ }
};

export function TourProvider({ children }) {
  const nav = useNavigate();
  const { pathname, search } = useLocation();
  // Which app the lesson is being walked on. The two builds share these lessons
  // but not their screens, so every list of steps has to be asked for by build —
  // see stepsFor. Read live rather than at mount: the breakpoint can be crossed
  // by resizing a window, and the tour must then be describing what is on screen.
  const build = useIsDesktop() ? 'desktop' : 'phone';
  const [lessonId, setLessonId] = useState(null);
  const [i, setI] = useState(0);
  // Which branch of the fork the agent took. Null until they answer it — the
  // branch steps are filtered out until then, and every branch step sits after
  // the fork, so there is nothing to show wrongly in the meantime.
  const [path, setPath] = useState(null);
  const [played, setPlayed] = useState(false);
  // Bumped when a simulated beat lands. Routed pages are keyed on it, so they
  // remount and re-read the mutated fixtures — otherwise the status flip the
  // step exists to show never reaches the screen the agent is looking at.
  const [remountKey, setRemountKey] = useState(0);

  // Read through a ref so the navigation effect can compare against the current
  // path without re-firing every time the path changes.
  // Compared WITH the query string: a step may target /forms/:id?tab=signing,
  // and ignoring the search would re-navigate (and remount) on every step.
  const pathnameRef = useRef('');
  pathnameRef.current = `${pathname}${search}`;

  const lesson = lessonId ? lessonById(lessonId) : null;
  // The walked list, not the authored one: a branch step from the road not taken
  // must not sit between two steps the agent can see.
  const steps = useMemo(() => stepsFor(lesson, path, build), [lesson, path, build]);
  // What the coach bar counts down. Before the fork is answered `steps` holds only
  // the shared ones, and showing that as the total would make the lesson appear to
  // GROW the moment the agent chose a branch. The two branches are the same
  // length, so standing in one of them is an honest total either way.
  const total = useMemo(() => stepsFor(lesson, path ?? 'listing', build).length, [lesson, path, build]);
  const step = steps[i] || null;

  // Demo mode is the sandbox. It must be on for the whole lesson and off the
  // instant it ends — including on unmount, or a crash mid-lesson would leave
  // the app serving fixtures over the agent's real data.
  useEffect(() => {
    setDemoMode(!!lessonId);
    // usePerformanceScopes caches its answer at MODULE level, for the whole
    // session. A team leader who reaches Learn Mode without a page reload carries
    // "you have a team" into the sandbox, where Home then asks for a team summary
    // the fixtures do not serve — and the lesson died on its first screen. Drop the
    // cache on the way in so the sandbox's own answer (no team) is used, and again
    // on the way out so the agent's real scopes are re-read.
    resetPerformanceScopes();
    // The class reserves a strip for the DEMO ribbon (see design.css) so the
    // banner sits ABOVE the app without covering it — including the app's own
    // full-screen overlays, which would otherwise start at viewport top.
    document.body.classList.toggle('learn-active', !!lessonId);
    return () => {
      setDemoMode(false);
      resetPerformanceScopes();
      document.body.classList.remove('learn-active');
    };
  }, [lessonId]);

  // Each step names the route it happens on. Driving navigation from here (rather
  // than asking the agent to find the page) is what makes it a tour instead of a
  // checklist — they still tap the real control once they arrive.
  useEffect(() => {
    // navState matters: some screens are only meaningful with the router state
    // the previous screen would have handed over — /forms/new without a
    // `fromListing` is a blank, unlinked form. Carrying it here is what makes a
    // step reachable by Back and by resume, not only by tapping forward into it.
    // Only when we are not already there. Re-navigating to the same path remounts
    // the route, which would wipe a half-filled booking form every time the agent
    // moved a step — several consecutive steps live on /forms/new.
    if (step?.route && step.route !== pathnameRef.current) {
      const st = typeof step.navState === 'function' ? step.navState(path) : step.navState;
      nav(step.route, { replace: false, state: st });
    }
    setPlayed(false);
  }, [step?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = useCallback((id, opts = {}) => {
    const l = lessonById(id);
    if (!l) return;
    const saved = readProgress()[id];
    // A lesson saved on the phone cannot be resumed by index here, or the other
    // way round: the two builds walk different numbers of steps, so step 9 is a
    // different step on each. Treat a build change as a fresh run rather than
    // dropping the agent somewhere the lesson never sent them.
    const fresh = opts.restart || !saved || saved.done || (saved.build && saved.build !== build);
    // A resumed lesson resumes on the branch it was being walked on. Without the
    // saved path the branch steps vanish from the list and the saved index lands
    // on a different step than the one the agent left.
    const p = fresh ? null : (saved.path ?? null);
    const list = stepsFor(l, p, build);
    // Resume by step ID first, index second. A release that inserts a step into
    // the middle of a lesson (Request receipt went into LESSON 2 between Request
    // invoice and Ready to claim) shifts every index after it, so a saved index
    // alone would silently resume an agent one step forward of where they left.
    // The id survives that; the index is the fallback for progress saved before
    // ids were recorded, and for the fork, where the branch steps only exist
    // after the answer.
    const byId = !fresh && saved.stepId ? list.findIndex((st) => st.id === saved.stepId) : -1;
    let from = fresh ? 0 : (byId >= 0 ? byId : Math.min(saved.step || 0, list.length - 1));
    // Resuming has the same constraint as Back: a step inside a block can only be
    // done from that block's beginning, because the screen it lives on is rebuilt
    // empty. Rewind to the block's first step so the agent redoes it with all the
    // data, rather than landing on "save it" over a form with nothing on it.
    const blk = list[from]?.block;
    if (blk) {
      from = list.findIndex((st) => st.block === blk);
      resetBlock(blk);
    }
    // Belt and braces: a step with no route at all cannot be navigated to.
    while (from > 0 && !list[from].route) from -= 1;
    // Each run starts from the same pretend deal, or a resumed lesson would
    // inherit whatever the last one left behind.
    // The lesson decides which pretend deal it gets — the spine's solo sale, or
    // the co-share's shared one, where the same house pays half the fee.
    resetFixtures(id);
    setDemoPath(p);
    // …then replay everything that had already happened. The later steps read a
    // sandbox shaped by earlier ones — there is no invoice to open until the HQ
    // beat that raises it has run — so resuming without this lands the agent on a
    // step pointing at a record that does not exist.
    for (let k = 0; k < from; k += 1) {
      const b = list[k].beat;
      if (b) applyBeat(b);
    }
    setLessonId(id);
    setPath(p);
    setI(from);
  }, [build]);

  const stop = useCallback(() => {
    setLessonId(null);
    setPath(null);
    setI(0);
    nav('/learn', { replace: true });
  }, [nav]);

  const next = useCallback(() => {
    if (!lesson) return;
    if (i + 1 >= steps.length) {
      const p = readProgress();
      p[lesson.id] = { step: steps.length - 1, stepId: steps[steps.length - 1]?.id, done: true, path, build };
      writeProgress(p);
      setLessonId(null);
      setPath(null);
      setI(0);
      nav('/learn', { replace: true });
      return;
    }
    const p = readProgress();
    p[lesson.id] = { step: i + 1, stepId: steps[i + 1]?.id, done: false, path, build };
    writeProgress(p);
    setI(i + 1);
  }, [lesson, steps, i, path, build, nav]);

  /**
   * Answer the fork. The branch steps appear in the list the moment this lands,
   * and because every one of them sits AFTER the fork, the index the agent is on
   * still points at the same step it did before.
   */
  const choose = useCallback((key) => {
    setPath(key);
    // The sandbox has to know too: a form written from scratch is linked to no
    // listing, so issuing it must not move a listing to Under Offer.
    setDemoPath(key);
    setI((n) => n + 1);
    if (lesson) {
      const p = readProgress();
      p[lesson.id] = { step: i + 1, done: false, path: key, build };
      writeProgress(p);
    }
  }, [lesson, i, build]);

  /**
   * Back, with one rule that matters: a block you can only do from the start has
   * to restart from the start.
   *
   * Several steps run on one screen whose state is not restorable — /forms/new is
   * always a fresh form, so stepping back into the middle of the create flow would
   * show the agent a "save it" step over an empty form with the parties they made
   * missing. When Back would re-enter a block from outside it, we rewind to that
   * block's FIRST step and reset the sandbox to match, so the agent genuinely does
   * it again rather than landing in a state that cannot exist.
   */
  const back = useCallback(() => {
    if (!lesson) return;
    const target = Math.max(0, i - 1);
    const targetBlock = steps[target]?.block;
    const leavingBlock = targetBlock && steps[i]?.block !== targetBlock;
    const land = (idx) => {
      // Back must persist too. Without this, exiting after a rewind resumes
      // FORWARD of where the agent actually was, silently skipping the block the
      // tour had just sent them back to redo.
      const p = readProgress();
      p[lesson.id] = { step: idx, stepId: steps[idx]?.id, done: false, path, build };
      writeProgress(p);
      setI(idx);
    };
    if (leavingBlock) {
      const first = steps.findIndex((st) => st.block === targetBlock);
      resetBlock(targetBlock);
      setRemountKey((n) => n + 1);
      land(first === -1 ? target : first);
      return;
    }
    land(target);
  }, [lesson, steps, i, path, build]);

  // A simulated beat mutates the fixtures, so the REAL page the agent is looking
  // at re-renders with the new status — that flip is the whole point of the step.
  const play = useCallback(() => {
    if (step?.beat) {
      applyBeat(step.beat);
      // Refresh IN PLACE rather than remounting. A remount restarts every
      // count-up from zero, so a figure that FALLS — PENDING emptying into PAID
      // when the payout lands — would show no movement at all. Pages that hold
      // money listen for this and re-read without blanking.
      window.dispatchEvent(new Event('cid:refresh'));
    }
    setPlayed(true);
  }, [step]);

  const value = useMemo(() => ({
    lesson, step, index: i, total,
    active: !!lesson, played, play, remountKey, path, choose,
    start, stop, next, back,
    // The overlay places its own chrome differently on each build — see
    // TourOverlay's frame measurement and the coach bar's geometry.
    desktop: build === 'desktop',
  }), [lesson, steps, total, step, i, played, play, remountKey, path, choose, start, stop, next, back, build]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
